import { Dialog } from '@base-ui/react/dialog';
import { useTranslation } from 'react-i18next';
import styles from './DiscardModal.module.css';

interface IDiscardModalProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Optional third action — e.g. the similar-name guard's "Use the existing
   *  spelling". Its presence switches the footer to the STACKED layout (full-
   *  width rows: altAction as the navy recommended exit, confirm as a neutral
   *  outline, Cancel as quiet text), sized for long quoted labels and gloved
   *  taps. Without it the classic delete row (red confirm) is untouched.
   *  Cancel/Esc/backdrop always mean "change nothing". */
  altAction?: { label: string; onClick: () => void };
  loading?: boolean;
  /**
   * DOM node to portal into. Defaults to `document.body`. Pass a node when the
   * modal must stay visible inside a native-fullscreen subtree (the Fullscreen
   * API paints only the fullscreen element, so a body portal is invisible).
   */
  container?: HTMLElement | null;
}

export function DiscardModal({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel,
  cancelLabel,
  altAction,
  loading = false,
  container,
}: IDiscardModalProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('common.delete');
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}
    >
      <Dialog.Portal container={container ?? undefined}>
        {/* forceRender: Base UI skips a NESTED dialog's backdrop (child of an
            open dialog) to avoid double dimming — but shading the host is
            exactly what anchors this confirmation to its flow. Nested uses
            (the similar-name guard, the AreaDrawer's delete confirm) gain the
            shade deliberately; non-nested uses always rendered it. */}
        <Dialog.Backdrop className={styles.backdrop} forceRender />
        <Dialog.Popup className={styles.panel}>
          <Dialog.Title className={styles.title}>{title}</Dialog.Title>
          {description && (
            <Dialog.Description className={styles.description}>
              {description}
            </Dialog.Description>
          )}
          {altAction ? (
            <div className={styles.actionsStacked}>
              <button type="button" className={styles.stackPrimaryBtn} onClick={altAction.onClick}>
                {altAction.label}
              </button>
              <button
                type="button"
                className={styles.stackGhostBtn}
                onClick={onConfirm}
                disabled={loading}
              >
                {resolvedConfirmLabel}
              </button>
              <Dialog.Close className={styles.stackTextBtn}>{resolvedCancelLabel}</Dialog.Close>
            </div>
          ) : (
            <div className={styles.actions}>
              <Dialog.Close className={styles.cancelBtn}>
                {resolvedCancelLabel}
              </Dialog.Close>
              <button
                type="button"
                className={styles.confirmBtn}
                onClick={onConfirm}
                disabled={loading}
              >
                {resolvedConfirmLabel}
              </button>
            </div>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
